import type { PrototypeCommand } from "@peer-start/platform-contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clearAllProductTourProgress } from "../../passport/components/product-tour-storage.js";
import {
  fetchAdministration,
  fetchBootstrap,
  fetchPersonas,
  fetchSession,
  selectSession,
  sendCommand,
} from "./client.js";

export const personaQueryKey = ["personas"] as const;
export const sessionQueryKey = ["session"] as const;
export const bootstrapQueryKey = ["bootstrap"] as const;
export const administrationQueryKey = ["administration"] as const;

export function usePersonas() {
  return useQuery({ queryKey: personaQueryKey, queryFn: fetchPersonas, staleTime: Number.POSITIVE_INFINITY });
}

export function useSession() {
  return useQuery({ queryKey: sessionQueryKey, queryFn: fetchSession });
}

export function useBootstrap(enabled: boolean) {
  return useQuery({ queryKey: bootstrapQueryKey, queryFn: fetchBootstrap, enabled });
}

export function useAdministration(enabled = true) {
  return useQuery({ queryKey: administrationQueryKey, queryFn: fetchAdministration, enabled });
}

export function useSelectSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: selectSession,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      queryClient.removeQueries({
        predicate: ({ queryKey }) => queryKey[0] !== sessionQueryKey[0] && queryKey[0] !== personaQueryKey[0],
      });
    },
  });
}

export function usePrototypeCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: PrototypeCommand) => sendCommand(command),
    onSuccess: async (_result, command) => {
      if (command.type === "prototype_reset") {
        clearAllProductTourProgress();
        await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
        await queryClient.invalidateQueries({ queryKey: personaQueryKey });
        queryClient.removeQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] !== sessionQueryKey[0] && queryKey[0] !== personaQueryKey[0],
        });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: bootstrapQueryKey });
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      await queryClient.invalidateQueries({ queryKey: personaQueryKey });
      await queryClient.invalidateQueries({ queryKey: administrationQueryKey });
    },
  });
}
